<template>
  <v-dialog
    :value="value"
    width="520"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>必要人数の設定</v-card-title>
      <v-card-text>
        <p class="text-caption grey--text mb-3">
          曜日ごとのデフォルト人数（空欄＝枠なし）
        </p>
        <div class="d-flex flex-wrap pt-1">
          <v-text-field
            v-for="(label, idx) in weekdayLabels"
            :key="`wd-${idx}`"
            v-model="weekdayCounts[idx]"
            :label="label"
            type="number"
            min="0"
            dense
            hide-details
            class="staffing-input me-2 mb-3"
          />
        </div>

        <p class="text-caption grey--text mt-5 mb-3">
          日別の上書き（削除するとデフォルトに戻ります）
        </p>
        <div
          v-for="(override, i) in overrides"
          :key="`ov-${override.date}`"
          class="d-flex align-center mb-2"
        >
          <span class="me-4">{{ formatDate(override.date) }}</span>
          <v-text-field
            v-model="override.requiredCount"
            type="number"
            min="0"
            dense
            hide-details
            class="staffing-input me-2"
          />
          <v-btn
            icon
            x-small
            :ripple="false"
            @click="overrides.splice(i, 1)"
          >
            <v-icon size="16">
              {{ icons.mdiTrashCanOutline }}
            </v-icon>
          </v-btn>
        </div>
        <div class="d-flex align-center mt-3 pt-1">
          <v-text-field
            v-model="newDate"
            type="date"
            label="日付"
            dense
            hide-details
            class="me-2"
          />
          <v-text-field
            v-model="newCount"
            type="number"
            min="0"
            label="人数"
            dense
            hide-details
            class="staffing-input me-2"
          />
          <v-btn
            small
            outlined
            :ripple="false"
            @click="addOverrideRow"
          >
            追加
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          :disabled="submitting"
          @click="$emit('input', false)"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          @click="save"
        >
          保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref, watch, getCurrentInstance,
} from '@vue/composition-api'
import { mdiTrashCanOutline } from '@mdi/js'
import StaffingTargetApi from '@/api/waiter/StaffingTarget'

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 現在の必要人数設定（{id, dayOfWeek, date, requiredCount} の配列）
    targets: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const weekdayLabels = WEEKDAY_LABELS
    const weekdayCounts = ref(['', '', '', '', '', '', ''])
    const overrides = ref([])
    const newDate = ref('')
    const newCount = ref('')
    const submitting = ref(false)

    // 開いたときに現在の設定からフォームを初期化する
    watch(() => props.value, isOpen => {
      if (!isOpen) return
      weekdayCounts.value = weekdayLabels.map((label, w) => {
        const target = props.targets.find(t => t.date == null && t.dayOfWeek === w)

        return target ? String(target.requiredCount) : ''
      })
      overrides.value = props.targets
        .filter(t => t.date != null)
        .map(t => ({ id: t.id, date: t.date, requiredCount: String(t.requiredCount) }))
      newDate.value = ''
      newCount.value = ''
    })

    const formatDate = dateStr => {
      if (!dateStr) return ''

      const date = new Date(dateStr)
      const dayNames = ['日', '月', '火', '水', '木', '金', '土']

      return `${date.getMonth() + 1}/${date.getDate()}（${dayNames[date.getDay()]}）`
    }

    const addOverrideRow = () => {
      if (!newDate.value || newCount.value === '') return
      if (overrides.value.find(o => o.date === newDate.value)) {
        vm.$toast.warning('同じ日付の上書きが既にあります')

        return
      }
      overrides.value.push({ id: null, date: newDate.value, requiredCount: newCount.value })
      newDate.value = ''
      newCount.value = ''
    }

    const save = async () => {
      submitting.value = true
      try {
        const ops = []

        // 曜日デフォルト: 空欄=削除、値あり=作成/更新
        weekdayLabels.forEach((label, w) => {
          const input = weekdayCounts.value[w]
          const value = input === '' || input == null ? null : Number(input)
          const existing = props.targets.find(t => t.date == null && t.dayOfWeek === w)
          if (value == null && existing) {
            ops.push(StaffingTargetApi.deleteStaffingTarget(existing.id))
          } else if (value != null && !existing) {
            ops.push(StaffingTargetApi.createStaffingTarget({ dayOfWeek: w, requiredCount: value }))
          } else if (value != null && existing && existing.requiredCount !== value) {
            ops.push(StaffingTargetApi.updateStaffingTarget({ id: existing.id, requiredCount: value }))
          }
        })

        // 日別上書き: ダイアログの一覧を正として差分適用
        const existingOverrides = props.targets.filter(t => t.date != null)
        existingOverrides.forEach(ex => {
          if (!overrides.value.find(o => o.id === ex.id)) {
            ops.push(StaffingTargetApi.deleteStaffingTarget(ex.id))
          }
        })
        overrides.value.forEach(o => {
          const value = Number(o.requiredCount)
          if (!o.id) {
            ops.push(StaffingTargetApi.createStaffingTarget({ date: o.date, requiredCount: value }))
          } else {
            const ex = existingOverrides.find(e => e.id === o.id)
            if (ex && ex.requiredCount !== value) {
              ops.push(StaffingTargetApi.updateStaffingTarget({ id: o.id, requiredCount: value }))
            }
          }
        })

        await Promise.all(ops)
        vm.$toast.success('必要人数を保存しました')
        emit('input', false)
        emit('saved')
      } catch (error) {
        vm.$toast.error('必要人数の保存に失敗しました。画面を更新して再度お試しください')
      } finally {
        submitting.value = false
      }
    }

    return {
      weekdayLabels,
      weekdayCounts,
      overrides,
      newDate,
      newCount,
      submitting,
      formatDate,
      addOverrideRow,
      save,
      icons: { mdiTrashCanOutline },
    }
  },
}
</script>

<style scoped>
.staffing-input {
  max-width: 80px;
  flex: 0 0 auto;
}
</style>
